import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import store from "../../state/Store";
import MessageLog from "../../types/system/MessageLog";
import PageSpecification from "../../types/system/PageSpecification";
import MessageLogRecipient from "../../types/system/MessageLogRecipient";

const imageRegex = /"(cid:(.+?))"/g;
const removePlaceHolder =
    '<table style="visibility: hidden; mso-hide: all;"><tr><td background="cid:tick.png"></td><td background="cid:cross.png"></td><td background="cid:question.png"></td></tr></table>';

// Define the type for our state store entry
type EmailLogState = {
    allData: Record<number, MessageLog[]>;
    pageData: MessageLog[];
    virtualSpec: PageSpecification;
    internalSpec: PageSpecification;
    status: string;
    openEmail?: MessageLog;
    openRecipients?: MessageLogRecipient[];
    openStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: EmailLogState = {
    allData: {},
    pageData: [],
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0,
    } as PageSpecification,
    internalSpec: {
        page: -1,
        size: 200,
        total: 0,
    } as PageSpecification,
    status: "idle",
    openStatus: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const emailLogSlice = createSlice({
    name: "emailLog",
    initialState: initialState,
    reducers: {
        setVirtualPageAndSize: (
            state,
            action: PayloadAction<PageSpecification>,
        ) => {
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;

            const internalPageIndex = Math.floor(
                (state.virtualSpec.page * state.virtualSpec.size) /
                    state.internalSpec.size,
            );
            const internalPage = state.allData[internalPageIndex];
            const translatedPageStart =
                state.virtualSpec.page * state.virtualSpec.size -
                internalPageIndex * state.internalSpec.size;
            const translatedPageEnd =
                translatedPageStart + state.virtualSpec.size;

            state.pageData =
                internalPage?.slice(
                    Math.min(translatedPageStart, internalPage.length),
                    Math.min(translatedPageEnd, internalPage.length),
                ) ?? [];

            state.virtualSpec.waiting =
                state.virtualSpec.loading && state.pageData.length === 0;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.emailLog?.allData)
                    for (let entry in rawData?.payload?.emailLog.allData)
                        allData[entry] = rawData?.payload?.emailLog.allData[
                            entry
                        ].map(item => new MessageLog(item));

                return {
                    allData: allData,
                    pageData:
                        rawData?.payload?.emailLog?.pageData?.map(
                            item => new MessageLog(item),
                        ) ?? [],
                    virtualSpec:
                        rawData?.payload?.emailLog?.virtualSpec ??
                        ({
                            page: 0,
                            size: 20,
                            total: 0,
                        } as PageSpecification),
                    internalSpec:
                        rawData?.payload?.emailLog?.internalSpec ??
                        ({
                            page: -1,
                            size: 200,
                            total: 0,
                        } as PageSpecification),
                    status: "idle",
                    openStatus: "idle",
                    error: null,
                };
            })
            .addCase(fetchEmailLogPage.pending, (state, action) => {
                state.status = "loading";
                state.virtualSpec.loading = true;
                state.internalSpec.loading = true;
            })
            .addCase(fetchEmailLogPage.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.virtualSpec.total = action.payload[1];
                state.internalSpec.total = action.payload[1];
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.allData[action.payload[0]] = action.payload[2].map(
                    item => new MessageLog(item),
                );

                const internalPageIndex = Math.floor(
                    (state.virtualSpec.page * state.virtualSpec.size) /
                        state.internalSpec.size,
                );
                const internalPage = state.allData[internalPageIndex];
                const translatedPageStart =
                    state.virtualSpec.page * state.virtualSpec.size -
                    internalPageIndex * state.internalSpec.size;
                const translatedPageEnd =
                    translatedPageStart + state.virtualSpec.size;

                state.pageData =
                    internalPage?.slice(
                        Math.min(translatedPageStart, internalPage.length),
                        Math.min(translatedPageEnd, internalPage.length),
                    ) ?? [];

                state.virtualSpec.waiting =
                    state.virtualSpec.loading && state.pageData.length === 0;
            })
            .addCase(fetchEmailLogPage.rejected, (state, action) => {
                state.status = "failed";
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.error = action.error.message;
            })
            .addCase(openEmailLog.pending, (state, action) => {
                state.openStatus = "loading";
            })
            .addCase(openEmailLog.fulfilled, (state, action) => {
                state.openEmail = new MessageLog(action.payload[0]);
                state.openRecipients = action.payload[1].map(
                    item => new MessageLogRecipient(item),
                );
                state.openStatus = "succeeded";
            })
            .addCase(openEmailLog.rejected, (state, action) => {
                state.openStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const { setVirtualPageAndSize } = emailLogSlice.actions;

// This is a thunk, they are used to load the state
const fetchEmailLogPage = createAsyncThunk(
    "emailLog/fetchEmailLogPage",
    async (fetchIndex: number) => {
        const { emailLog } = store.getState().db;
        const response = await Promise.all([
            Datastore.getType<number>(`api/MessagingLogs/Email/Count`),
            Datastore.getType<MessageLog[]>(
                `api/MessagingLogs/Email/Page/${fetchIndex}/Size/${emailLog.internalSpec.size}`,
            ),
        ]);
        const result = [fetchIndex, response[0], response[1]] as [
            number,
            number,
            MessageLog[],
        ];
        return result;
    },
);
const evaluatePrivateData = createAsyncThunk(
    "emailLog/evaluatePrivateData",
    async (specification: PageSpecification, thunkAPI): Promise<void> => {
        const { emailLog } = store.getState().db;
        const requiredInternalPage = Math.max(
            Math.ceil(
                ((specification.page + 1) * specification.size - 1) /
                    emailLog.internalSpec.size,
            ) - 1,
            0,
        );

        if (emailLog.status !== "loading") {
            if (!emailLog.allData[requiredInternalPage]) {
                await thunkAPI.dispatch(
                    fetchEmailLogPage(requiredInternalPage),
                );
            } else {
                const direction =
                    specification.page > emailLog.virtualSpec.page
                        ? 1
                        : specification.page < emailLog.virtualSpec.page
                        ? -1
                        : 0;

                if (direction !== 0) {
                    const pageGap =
                        direction > 0
                            ? (emailLog.internalSpec.size -
                                  (specification.page * specification.size -
                                      requiredInternalPage *
                                          emailLog.internalSpec.size)) /
                              specification.size
                            : (specification.page * specification.size -
                                  requiredInternalPage *
                                      emailLog.internalSpec.size) /
                              specification.size;

                    if (pageGap < 4) {
                        if (
                            direction < 0 &&
                            requiredInternalPage > 0 &&
                            !emailLog.allData[requiredInternalPage - 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchEmailLogPage(requiredInternalPage - 1),
                            );
                        } else if (
                            direction > 0 &&
                            !emailLog.allData[requiredInternalPage + 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchEmailLogPage(requiredInternalPage + 1),
                            );
                        }
                    }
                }
            }
        }
    },
);
export const getEmailLogPage = createAsyncThunk(
    "emailLog/getEmailLogPage",
    async (_, thunkAPI): Promise<void> => {
        const { emailLog } = store.getState().db;
        await thunkAPI.dispatch(evaluatePrivateData(emailLog.virtualSpec));
    },
);
export const setEmailLogPageAndSize = createAsyncThunk(
    "emailLog/setEmailLogPageAndSize",
    async (specification: PageSpecification, thunkAPI) => {
        await thunkAPI.dispatch(evaluatePrivateData(specification));
        thunkAPI.dispatch(setVirtualPageAndSize(specification));
    },
);
export const openEmailLog = createAsyncThunk(
    "emailLog/openEmailLog",
    async (email: MessageLog, thunkAPI) => {
        const message = await Promise.all([
            Datastore.getType<MessageLog>(
                `api/MessagingLogs/Internal/${email.id}`,
            ),
            Datastore.getType<any[]>(
                `api/MessagingLogs/Internal/${email.id}/Recipients`,
            ),
        ]);

        message[0].body = message[0].body.replace(removePlaceHolder, "");
        const matches = [...message[0].body.matchAll(imageRegex)];
        const images = [];

        for (const match of matches)
            if (!images.find(item => item.match[2] === match[2]))
                images.push({
                    match: match,
                    promise: Datastore.getType<MessageLogRecipient>(
                        `api/MessagingLogs/${email.id}/Attachments/${match[2]}`,
                    ),
                });

        const responses = await Promise.all(images.map(item => item.promise));

        images.forEach(
            (item, index) =>
                (message[0].body = message[0].body.replace(
                    new RegExp(item.match[1], "g"),
                    `data:${responses[index].mime};base64,${responses[index].content}`,
                )),
        );

        return [message[0], message[1]] as [MessageLog, MessageLogRecipient[]];
    },
);

// We have to export the reducer as this is added to the state store
export default emailLogSlice.reducer;
